import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Checkbox, FormControlLabel, Button, FormGroup, FormControl, FormLabel, TextareaAutosize, Stepper, Step, StepLabel, Box, Chip, FormHelperText } from '@material-ui/core';
import MultipleSelect from '~/components/Select/MultipleSelect';
import CustomItemPropSelect from '~/containers/Page_Gis/Page_Info/Page_GisPier/CustomItemPropSelect';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';



const steps = ['基本資料 (Basic Info)', '風場選擇 (Wind Fields)'];

const ReportEditorDialog = ({ open, onClose, onSave, mode, data, windFields = [] }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [user, setUser] = useState({
        user_name: '',
        email: '',
        organization: '',
        subscribe_status_for_daily_report: false,
        subscribe_status_for_weekly_report: false,
        subscribe_status_for_monthly_report: false,
        note: '',
        subscribe_windcontract_ids: []
    });
    const [errors, setErrors] = useState({});
    const [windFieldOptions, setWindFieldOptions] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);

    useEffect(() => {
        if (mode === 'edit' || mode === 'view') {
            setUser({
                user_name: data.user_name,
                email: data.email,
                organization: data.organization || '',
                subscribe_status_for_daily_report: data.subscribe_status_for_daily_report === '1',
                subscribe_status_for_weekly_report: data.subscribe_status_for_weekly_report === '1',
                subscribe_status_for_monthly_report: data.subscribe_status_for_monthly_report === '1',
                note: data.note || '',
                subscribe_windcontract_ids: data.subscribe_windcontract_ids || []
            });
            if (data.subscribe_windcontract_ids && windFields) {
                const initialSelectedItems = windFields
                    .filter(field => data.subscribe_windcontract_ids.includes(field.id))
                    .map(field => ({
                        value: field.id,
                        name: field.windfield_simplename
                    }));
                setSelectedItems(initialSelectedItems);
            }
        }
    }, [mode, data, windFields]);

    useEffect(() => {
        if (windFields) {
            setWindFieldOptions(windFields.map(field => ({
                value: field.id,
                name: field.windfield_simplename
            })));
        }
    }, [windFields]);

    const handleChange = (key, value) => {
        setUser(prevUser => ({
            ...prevUser,
            [key]: value
        }));
    };

    const handleChangeItemSelection = (e, type) => {
        const newSelectedItems = e.target.value;
        setSelectedItems(newSelectedItems);
        // Update user.subscribe_windcontract_ids with the new selection
        const newWindContractIds = newSelectedItems.map(item => item.value);
        handleChange('subscribe_windcontract_ids', newWindContractIds);
    };

    const handleDeleteItem = (item) => {
        const updatedSelectedItems = selectedItems.filter((selectedItem) => selectedItem.value !== item.value);
        setSelectedItems(updatedSelectedItems);
        // Update user.subscribe_windcontract_ids when deleting an item
        const updatedWindContractIds = updatedSelectedItems.map(item => item.value);
        handleChange('subscribe_windcontract_ids', updatedWindContractIds);
    };

    const handleNext = () => {
        const { user_name, email } = user;
        const { subscribe_status_for_daily_report, subscribe_status_for_weekly_report, subscribe_status_for_monthly_report } = user;
        const newErrors = {};

        if (!user_name) {
            newErrors.user_name = '使用者名稱是必填的 (Username is required)';
        }
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            newErrors.email = '請輸入有效的電子郵件地址 (Please enter a valid email address)';
        }
        if (!subscribe_status_for_daily_report && !subscribe_status_for_weekly_report && !subscribe_status_for_monthly_report) {
            newErrors.subscribe_status_for_daily_report = '請至少選擇一項報表 (Please select at least one report type)';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
        } else {
            setActiveStep(1);
        }
    };

    const handleBack = () => {
        setActiveStep(0);
    };

    const handleSave = () => {
        if (user.subscribe_windcontract_ids.length === 0) {
            setErrors({ wind_fields: '請至少選擇一個風場 (Please select at least one wind field)' });
            return;
        }

        onSave(user);
    };

    const renderBasicInfo = () => (
        <>
            <TextField
                label="使用者名稱 (User Name)"
                value={user.user_name}
                onChange={(e) => handleChange('user_name', e.target.value)}
                fullWidth
                margin="normal"
                variant="outlined"
                disabled={mode === 'view'}
                error={!!errors.user_name}
                helperText={errors.user_name}
                required
            />
            <TextField
                label="使用者電郵 (Email)"
                value={user.email}
                onChange={(e) => handleChange('email', e.target.value)}
                fullWidth
                margin="normal"
                variant="outlined"
                disabled={mode === 'view'}
                error={!!errors.email}
                helperText={errors.email}
                required
            />
            <TextField
                label="組織 (Organization)"
                value={user.organization}
                onChange={(e) => handleChange('organization', e.target.value)}
                fullWidth
                margin="normal"
                variant="outlined"
                disabled={mode === 'view'}
            />
            <FormControl component="fieldset" error={!!errors.subscribe_status_for_daily_report}>
                <FormLabel component="legend">報表訂閱狀態 (Report Subscription Status)</FormLabel>
                <FormGroup>
                    <FormControlLabel
                        control={<Checkbox checked={user.subscribe_status_for_daily_report} onChange={(e) => handleChange('subscribe_status_for_daily_report', e.target.checked)} disabled={mode === 'view'} />}
                        label="日報表 (Daily Report)"
                    />
                    <FormControlLabel
                        control={<Checkbox checked={user.subscribe_status_for_weekly_report} onChange={(e) => handleChange('subscribe_status_for_weekly_report', e.target.checked)} disabled={mode === 'view'} />}
                        label="週報表 (Weekly Report)"
                    />
                    <FormControlLabel
                        control={<Checkbox checked={user.subscribe_status_for_monthly_report} onChange={(e) => handleChange('subscribe_status_for_monthly_report', e.target.checked)} disabled={mode === 'view'} />}
                        label="月報表 (Monthly Report)"
                    />
                </FormGroup>
                {errors.subscribe_status_for_daily_report && (
                    <div style={{ color: '#f44336', fontSize: '0.75rem', marginTop: '3px', marginLeft: '14px' }}>
                        {errors.subscribe_status_for_daily_report}
                    </div>
                )}
            </FormControl>
            <TextareaAutosize
                rowsMin={3}
                placeholder="備註說明 (Note)"
                value={user.note}
                onChange={(e) => handleChange('note', e.target.value)}
                style={{ width: '95%', marginTop: '16px', padding: '8px', fontSize: '14px' }}
                disabled={mode === 'view'}
            />
        </>
    );

    const renderWindFields = () => (
        <Box sx={{ p: 2 }}>
            <CustomItemPropSelect
                style={{ width: 230, border: '1px solid #999999', margin: '8px 0px' }}
                label="訂閱風場 (Wind Contracts)"
                value={selectedItems}
                onChange={handleChangeItemSelection}
                options={windFieldOptions}
                selectedItems={selectedItems}
                type="windContracts"
                disabled={mode === 'view'}
            />

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'flex-start', marginTop: '8px' }}>
                {selectedItems.map((item, idx) => (
                    <Chip
                        key={item.value}
                        label={item.name}
                        onDelete={!(mode === 'view') ? () => handleDeleteItem(item): () => {}}
                        deleteIcon={(mode === 'view') ? <CheckCircleIcon />: undefined}
                        color="secondary"
                        variant="outlined"
                    />
                ))}
            </div>
            {errors.wind_fields && (
                <FormHelperText error>
                    {errors.wind_fields}
                </FormHelperText>
            )}
        </Box>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {mode === 'add' ? '新增資料 (Add New Data)' : mode === 'edit' ? '編輯資料 (Edit Data)' : '檢視資料 (View Data)'}
            </DialogTitle>
            <DialogContent>
                <Stepper activeStep={activeStep} style={{ marginBottom: '20px' }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
                {activeStep === 0 ? renderBasicInfo() : renderWindFields()}
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onClose} color="default">
                    {mode === 'view' ? '關閉 (Close)' : '取消 (Cancel)'}
                </Button>
                {activeStep === 1 && (
                    <Button variant="outlined" onClick={handleBack} color="primary">
                        上一步 (Back)
                    </Button>
                )}
                {activeStep === 0 && (
                    <Button variant="outlined" onClick={handleNext} color="primary">
                        下一步 (Next)
                    </Button>
                )}
                {mode !== 'view' && activeStep === 1 && (
                    <Button variant="outlined" onClick={handleSave} color="primary">
                        儲存 (Save)
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ReportEditorDialog;
